// Currency formatting, derived from the business's registered country (or an
// explicit override). Prices are stored as plain numbers everywhere - this is the
// single place that turns a number into a localized money string, so a US business
// shows "$1,250.00" and a PH business shows "₱1,250.00" off the same data.
//
// Resolution order for the active currency: explicit override
// (localStorage `pointbunny_currency`, set from businesses.settings.currency) ->
// else derived from the business country (`pointbunny_business_country`). Reading
// from localStorage means popups (KDS/CFD) and the main app all agree without
// threading state through every call site - same pattern as orderTypeLabel.

// Country -> ISO 4217 currency code. Keep in sync with COUNTRY_NAMES in
// countries.js and the onboarding country <select>.
const COUNTRY_CURRENCY = {
  US: 'USD', CA: 'CAD', GB: 'GBP', AU: 'AUD', NZ: 'NZD', PH: 'PHP', SG: 'SGD',
  MY: 'MYR', ID: 'IDR', TH: 'THB', VN: 'VND', IN: 'INR', JP: 'JPY', KR: 'KRW',
  CN: 'CNY', HK: 'HKD', TW: 'TWD', AE: 'AED', SA: 'SAR', NG: 'NGN', GH: 'GHS',
  KE: 'KES', ZA: 'ZAR', MX: 'MXN', BR: 'BRL', AR: 'ARS', CO: 'COP', DE: 'EUR',
  FR: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', SE: 'SEK', NO: 'NOK', DK: 'DKK',
};

export function currencyForCountry(country) {
  return COUNTRY_CURRENCY[String(country || '').toUpperCase()] || 'USD';
}

// The currency code in effect right now (override wins, else country-derived).
export function activeCurrencyCode() {
  let override = null, country = '';
  try {
    override = localStorage.getItem('pointbunny_currency');
    country  = localStorage.getItem('pointbunny_business_country') || '';
  } catch (_) { /* SSR / no storage */ }
  return override || currencyForCountry(country);
}

// Cache of Intl formatters keyed by currency code (cheap reuse).
const _fmtCache = {};
function _intl(code) {
  if (!_fmtCache[code]) {
    // en-US locale gives comma grouping + symbol-first, matching our markets
    // (US/PH/UK/AU/NZ/CA). `currency` drives the symbol + correct decimal count
    // (e.g. JPY/KRW = 0 decimals) automatically.
    _fmtCache[code] = new Intl.NumberFormat('en-US', { style: 'currency', currency: code });
  }
  return _fmtCache[code];
}

// How many fraction digits this currency uses (2 for USD/PHP, 0 for JPY, etc.).
function _decimals(code) {
  try { return _intl(code).resolvedOptions().maximumFractionDigits; }
  catch (_) { return 2; }
}

// Format a number as money. `ascii:true` is for thermal/ESC-POS receipts that
// can't render symbols like ₱/€/£ - it prints the currency CODE instead
// ("PHP 1,250.00"). `code` defaults to the active currency.
export function formatMoney(n, { code = activeCurrencyCode(), ascii = false } = {}) {
  const value = Number(n) || 0;
  if (ascii) {
    const d = _decimals(code);
    const num = value.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
    return `${code} ${num}`;
  }
  try {
    return _intl(code).format(value);
  } catch (_) {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

// Short money for tight spaces like stat tiles ("$1.08M", "₱250K"). Amounts
// under 100,000 use the full formatMoney since they already fit; pair the
// short form with the exact formatMoney value somewhere nearby.
export function formatMoneyCompact(n, { code = activeCurrencyCode() } = {}) {
  const value = Number(n) || 0;
  if (Math.abs(value) < 100000) return formatMoney(value, { code });
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, notation: 'compact', maximumFractionDigits: 2 }).format(value);
  } catch (_) {
    return formatMoney(value, { code });
  }
}

// Just the symbol (e.g. "$", "₱", "£") - for input prefixes and inline labels.
export function currencySymbol(code = activeCurrencyCode()) {
  try {
    const part = _intl(code).formatToParts(0).find(p => p.type === 'currency');
    return part?.value || '$';
  } catch (_) {
    return '$';
  }
}
